import React, {useEffect, useRef, useState} from "react";
import {useDropzone} from "react-dropzone";
import makeStyles from "@material-ui/core/styles/makeStyles";
import clsx from "clsx";
import {read, utils} from "xlsx";
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import api from "../../services/api";
import {useHistory} from "react-router-dom";
import toastError from "../../errors/toastError";
import {toast} from "react-toastify";
import {getTokens} from "../../styles/tokens";
import {TableCard} from "../FormFields/panel";

function WorksheetToDatagrid(ws) {
    /* create an array of arrays - mantendo formatação de células */
    const rows = utils.sheet_to_json(ws, {header: 1, defval: "", raw: false});

    /* create column array */
    const range = utils.decode_range(ws["!ref"] || "A1");
    const columns = Array.from({length: range.e.c + 1}, (_, i) => ({
        key: String(i), // RDG will access row["0"], row["1"], etc
        name: utils.encode_col(i), // the column labels will be A, B, etc
        //editor: textEditor // enable cell editing
    }));

    return {rows, columns}; // these can be fed to setRows / setColumns
}


const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        root: {
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
        },
        /* ---------- passos ---------- */
        steps: {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "6px 10px",
            margin: "0 0 14px",
            padding: 0,
            listStyle: "none",
            fontFamily: t.font,
            fontSize: 13,
            color: t.textMuted,
        },
        step: {
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            "& + &::before": {
                content: '""',
                width: 18,
                height: 1,
                marginRight: 2,
                backgroundColor: t.lineStrong,
            },
        },
        stepNumber: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: "50%",
            fontSize: 12,
            fontWeight: 600,
            border: `1px solid ${t.lineStrong}`,
            color: t.textSecondary,
            backgroundColor: t.paper,
        },
        stepActive: {
            color: t.text,
            fontWeight: 600,
            "& $stepNumber": {
                borderColor: t.brand,
                backgroundColor: t.brand,
                color: t.brandOn,
            },
        },
        stepDone: {
            color: t.textSecondary,
            "& $stepNumber": {
                borderColor: "transparent",
                backgroundColor: t.tint.medium,
                color: t.mode === "dark" ? t.text : t.brand,
            },
        },
        /* ---------- cartões ---------- */
        card: {
            backgroundColor: t.paper,
            border: `1px solid ${t.line}`,
            borderRadius: t.radius.md,
            boxShadow: t.shadow.card,
        },
        centerCard: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 14,
            padding: "28px 24px",
            fontFamily: t.font,
            [theme.breakpoints.down("xs")]: { padding: "20px 16px" },
        },
        dropzone: {
            width: "100%",
            maxWidth: 560,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            padding: "32px 20px",
            borderRadius: t.radius.md,
            border: `1.5px dashed ${t.lineStrong}`,
            backgroundColor: t.muted,
            cursor: "pointer",
            outline: "none",
            transition: "border-color 150ms ease, background-color 150ms ease",
            "&:hover, &:focus-visible": {
                borderColor: t.brand,
                backgroundColor: t.tint.soft,
            },
        },
        dropIcon: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            marginBottom: 6,
            borderRadius: t.radius.md,
            backgroundColor: t.tint.soft,
            color: t.mode === "dark" ? t.text : t.brand,
            "& svg": { fontSize: 26 },
        },
        dropTitle: {
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            color: t.text,
        },
        dropHint: {
            margin: 0,
            fontSize: 13,
            lineHeight: 1.55,
            color: t.textSecondary,
            maxWidth: 420,
        },
        cardTitle: {
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: t.text,
        },
        cardText: {
            margin: 0,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: t.textSecondary,
            maxWidth: 460,
        },
        errorIcon: {
            backgroundColor: t.status.down.bg,
            color: t.status.down.fg,
        },
        resultList: {
            width: "100%",
            maxWidth: 460,
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "grid",
            gap: 8,
            textAlign: "left",
        },
        resultItem: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            borderRadius: t.radius.sm,
            fontSize: 13.5,
            fontWeight: 600,
            lineHeight: 1.45,
            backgroundColor: t.status.neutral.bg,
            color: t.status.neutral.fg,
            "& svg": { fontSize: 20, flex: "none" },
        },
        resultOk: { backgroundColor: t.status.ok.bg, color: t.status.ok.fg },
        resultWarn: { backgroundColor: t.status.warn.bg, color: t.status.warn.fg },
        /* ---------- mapeamento da planilha ---------- */
        options: {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "6px 20px",
            padding: "8px 14px",
            marginBottom: 14,
            fontFamily: t.font,
            "& .MuiFormControlLabel-root": { marginRight: 0 },
            "& .MuiFormControlLabel-label": { fontSize: 13.5, fontWeight: 600, color: t.text },
        },
        optionsHint: {
            flex: "1 1 320px",
            margin: 0,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: t.textSecondary,
        },
        xlsTable: {
            "& .MuiTableCell-root": { whiteSpace: "nowrap" },
            "& .MuiTableCell-head": { verticalAlign: "bottom" },
        },
        checkCell: {
            width: 44,
            paddingRight: 0,
            "& .MuiCheckbox-root": { padding: 6 },
        },
        colHead: {
            display: "flex",
            flexDirection: "column",
            gap: 6,
            minWidth: 160,
        },
        colName: {
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            maxWidth: 220,
            "& b": { fontWeight: 600 },
            "& span": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 400,
                letterSpacing: 0,
                color: t.textMuted,
            },
        },
        select: {
            minWidth: 160,
            "& .MuiSelect-root": { fontSize: 13, fontWeight: 400, letterSpacing: 0 },
        },
        placeholder: { color: t.textMuted },
        actions: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 14,
            fontFamily: t.font,
        },
        actionsInfo: {
            marginRight: "auto",
            fontSize: 13,
            color: t.textSecondary,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
        },
        error: {
            color: t.mode === "dark" ? t.status.down.fg : t.semantic.error,
            fontSize: 13,
        },
    };
});

const STEPS = ["Enviar planilha", "Relacionar colunas", "Resultado"];

const ContactImport = () => {
    const [rows, setRows] = useState(null);
    const [columns, setColumns] = useState(null);
    const classes = useStyles();
    const history = useHistory();
    const [contactFieldsAvailable, setContactFieldsAvailable] = useState([]);
    const [columnValue, setColumnValue] = useState({});
    const [selectedFields, setSelectedFields] = useState({}); // Para rastrear seleções únicas

    const [openingFile, setOpeningFile] = useState(false);
    const [selection, setSelection] = useState({});
    const [invalidFile, setInvalidFile] = useState(false);
    const [error, setError] = useState(null);
    const [countCreated, setCountCreated] = useState(0);
    const [countIgnored, setCountIgnored] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [imported, setImported] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [validateContact, setValidateContact] = useState(true);

    // Função para converter número serial do Excel para data JavaScript
    const excelSerialToDate = (serialNumber) => {
        if (!serialNumber || serialNumber < 0) return null;
        
        // Excel armazena datas desde 01/01/1900
        // Serial 1 = 01/01/1900
        // Serial 2 = 02/01/1900
        
        // Diferença em dias entre 01/01/1900 e 01/01/1970 (Unix epoch)
        const daysDifference = 25569;
        
        // Converter serial para Unix timestamp (millisegundos)
        const timestamp = (serialNumber - daysDifference) * 86400000;
        
        // Criar Date object
        const date = new Date(timestamp);
        
        // Verificar se é válido
        if (isNaN(date.getTime())) {
            console.warn('Invalid Excel serial:', serialNumber);
            return null;
        }
        
        return date;
    };

    // Função helper para detectar e corrigir formato de data MM/DD vs DD/MM
    const parseAndFixDateFormat = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        
        // Processar datas com formato MM/DD/YYYY (Excel americano) e inverter para DD/MM/YYYY
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const first = parseInt(parts[0]);
                const second = parseInt(parts[1]);
                const year = parts[2];
                
                // Sempre assumir que é MM/DD/YYYY do Excel e converter para DD/MM/YYYY
                // Excel usa formato americano mesmo quando digitamos DD/MM
                console.log(`📅 Invertendo MM/DD para DD/MM: ${first}/${second}/${year}`);
                
                // Retornar como DD/MM/YYYY (invertendo)
                return new Date(parseInt(year), first - 1, second);
            }
        }
        
        return new Date(dateStr);
    };
    const contactFields = [
        {id: "name", label: "Nome", required: true},
        {id: "number", label: "Número", required: true},
        {id: "email", label: "E-mail", required: false},
        {id: "birthDate", label: "Data de Nascimento", required: false},
        {id: "tags", label: "Tags", required: false},
        {id: "carteira", label: "Carteira", required: false},
    ];

    useEffect(() => {
        setContactFieldsAvailable(contactFields);
    }, []);

    const processImport = async () => {
        setUploading(true);
        setCountCreated(0);
        setCountIgnored(0);

        console.log(selection)

        if (!selection.number) {
            toastError("Não foi selecionado o campo de número do contato");
            setUploading(false);
            return;
        }

        if (!selection.name) {
            toastError("Não foi selecionado o campo de nome do contato");
            setUploading(false);
            return;
        }

        if (Object.keys(selectedRows).length === 0) {
            toastError("Nenhum contato selecionado");
            setUploading(false);
            return;
        }

        if (rows?.length > 1) {
            let importedCount = 0;
            let ignoredCount = 0;

            for (let index = 1; index < rows.length; index++) {
                if (selectedRows[index]) { // Importar apenas as linhas selecionadas
                    const item = rows[index];
                    const contactData = {};

                    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                        const column = columns[columnIndex];
                        const selectedField = columnValue[column.key];

                        if (selectedField) {
                            // Garantir que número seja sempre string
                            if (selectedField === 'number' && item[columnIndex]) {
                                contactData[selectedField] = String(item[columnIndex]).trim();
                            } else if (selectedField === 'birthDate' && item[columnIndex] !== null && item[columnIndex] !== undefined && item[columnIndex] !== '') {
                                // Processar data de nascimento
                                let dateValue = item[columnIndex];
                                let parsedDate = null;
                                
                                try {
                                    // Excel armazena datas como números seriais (dias desde 30/12/1899)
                                    if (typeof dateValue === 'number' && dateValue > 1 && dateValue < 100000) {
                                        // Converter número serial do Excel para JavaScript Date
                                        parsedDate = excelSerialToDate(dateValue);
                                        console.log('Convertendo Excel serial:', dateValue, 'para data:', parsedDate);
                                    } else if (typeof dateValue === 'string') {
                                        // Processar strings de data
                                        // Usar função helper para detectar e corrigir formato
                                        parsedDate = parseAndFixDateFormat(dateValue);
                                        console.log('📅 Data parseada:', dateValue, '→', parsedDate);
                                    }
                                    
                                    // Validar e converter para formato YYYY-MM-DD
                                    if (parsedDate && !isNaN(parsedDate.getTime()) && parsedDate <= new Date()) {
                                        contactData[selectedField] = parsedDate.toISOString().split('T')[0];
                                        console.log('✅ Data processada com sucesso:', dateValue, '→', contactData[selectedField]);
                                    } else if (parsedDate) {
                                        console.warn('⚠️ Data de nascimento inválida ou futura:', dateValue);
                                    }
                                } catch (error) {
                                    console.warn('❌ Erro ao processar data de nascimento:', dateValue, error);
                                }
                            } else {
                                contactData[selectedField] = item[columnIndex];
                            }
                        }
                    }

                    // Verificar se os campos obrigatórios estão presentes
                    const missingRequiredFields = contactFields.some(field =>
                        field.required && (!contactData[field.id] || contactData[field.id] === '')
                    );

                    if (missingRequiredFields) {
                        ignoredCount++;
                        continue;
                    }

                    // Formatar número se necessário
                    if (contactData.number) {
                        // Remover caracteres não numéricos
                        contactData.number = contactData.number.toString().replace(/\D/g, '');

                        // Garantir que tenha o formato correto com código do país
                        if (!contactData.number.startsWith('55') && contactData.number.length <= 11) {
                            contactData.number = `55${contactData.number}`;
                        }
                    }

                    try {
                        console.log("Enviando dados para importação:", contactData);
                        const response = await api.post('/contactsImport', {
                            ...contactData,
                            validateContact: validateContact ? "true" : "false",
                        });

                        console.log("Resposta da API:", response);

                        if (response.status === 200) {
                            importedCount++;
                        } else {
                            ignoredCount++;
                        }
                    } catch (error) {
                        console.error("Erro na importação:", error);
                        ignoredCount++;
                    }
                }
            }

            setCountCreated(importedCount);
            setCountIgnored(ignoredCount);
            setValidateContact(false);
            setSelectedRows({});
            setImported(true);
            setUploading(false);

            if (importedCount > 0) {
                if (ignoredCount === 0) {
                    toast.success(`Importação realizada com sucesso! ${importedCount} contatos importados.`);
                } else {
                    toast.warn(`Importação concluída: ${importedCount} contatos importados e ${ignoredCount} ignorados.`);
                }
            } else {
                toast.error("Falha na importação. Nenhum contato foi importado.");
            }
        }
    };

    const onChangeFile = (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        setOpeningFile(true);
        setInvalidFile(false);
        setImported(false);
        setUploading(false);
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = function (e) {
            try {
                const data = e.target.result;
                const wb = read(data);
                const ws = wb.Sheets[wb.SheetNames[0]];
                const {rows, columns} = WorksheetToDatagrid(ws);
                setRows(rows);
                setColumns(columns);
                setOpeningFile(false);
            } catch (e) {
                console.error(e);
                setInvalidFile(true);
                setOpeningFile(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSelectChange = (event) => {
        const newValue = event.target.value;
        const columnKey = event.target.name;

        if (columnValue[columnKey]) {
            const oldValue = columnValue[columnKey];
            setSelectedFields((prevSelectedFields) => {
                const newSelectedFields = {...prevSelectedFields};
                delete newSelectedFields[oldValue];
                return newSelectedFields;
            });
        }

        if (newValue === "") {
            setColumnValue((prevColumnValue) => {
                const newColumnValue = {...prevColumnValue};
                delete newColumnValue[columnKey];
                return newColumnValue;
            });
            setSelection((prevSelection) => {
                const newSelection = {...prevSelection};
                Object.keys(newSelection).forEach((key) => {
                    if (newSelection[key] === columnKey) {
                        delete newSelection[key];
                    }
                });
                return newSelection;
            });
            return;
        }

        if (selectedFields[newValue]) {
            toastError(`O campo ${newValue} já foi selecionado.`);
            return;
        }

        setSelection((selection) => ({...selection, [newValue]: columnKey}));
        setSelectedFields((prevSelectedFields) => ({...prevSelectedFields, [newValue]: columnKey}));
        setColumnValue((columnValue) => ({...columnValue, [columnKey]: newValue}));
    };


    const renderSelectbox = (column) => {
        return (
            <TextField
                select
                size="small"
                variant="outlined"
                className={classes.select}
                value={columnValue[column.key] || ""}
                name={column.key}
                onChange={handleSelectChange}
                inputProps={{"aria-label": `Campo do contato para a coluna ${column.name}`}}
                SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                        const field = contactFieldsAvailable.find((f) => f.id === value);
                        return field ? field.label : <span className={classes.placeholder}>Não importar</span>;
                    },
                }}
            >
                <MenuItem value="">Não importar</MenuItem>
                {contactFieldsAvailable.map((contactField) => (
                    <MenuItem key={contactField.id} value={contactField.id}>
                        {contactField.label}{contactField.required ? " *" : ""}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderXls = () => {
        return (
            <TableCard fill>
                <Table stickyHeader size="small" className={classes.xlsTable}>
                    <TableHead key={columns.length}>
                        <TableRow>
                            <TableCell className={classes.checkCell}>
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    inputProps={{"aria-label": "Selecionar todas as linhas"}}
                                    checked={Object.keys(selectedRows).length === rows.length - 1} // Se todas as linhas, exceto a primeira, estiverem marcadas
                                    onChange={(event) => {
                                        const isChecked = event.target.checked;
                                        const newSelectedRows = {};
                                        if (isChecked) {
                                            // Marcar todas as linhas, exceto a primeira
                                            for (let i = 1; i < rows.length; i++) {
                                                newSelectedRows[i] = true;
                                            }
                                        }
                                        setSelectedRows(newSelectedRows);
                                    }}
                                />
                            </TableCell>
                            {columns.map((column, columnIndex) => (
                                <TableCell key={column.key}>
                                    <div className={classes.colHead}>
                                        {/* letra da coluna + título da 1ª linha da planilha (que não é importada) */}
                                        <div className={classes.colName}>
                                            <b>{column.name}</b>
                                            {rows[0] && rows[0][columnIndex] !== undefined && rows[0][columnIndex] !== "" && (
                                                <span title={String(rows[0][columnIndex])}>{String(rows[0][columnIndex])}</span>
                                            )}
                                        </div>
                                        {renderSelectbox(column)}
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => rowIndex === 0 ? null : (
                            <TableRow key={rowIndex} selected={!!selectedRows[rowIndex]}>
                                {rowIndex !== 0 && (
                                    <TableCell className={classes.checkCell}>
                                        <Checkbox
                                            color="primary"
                                            size="small"
                                            inputProps={{"aria-label": `Selecionar linha ${rowIndex}`}}
                                            checked={!!selectedRows[rowIndex]}
                                            onChange={() => {
                                                setSelectedRows((prevSelectedRows) => ({
                                                    ...prevSelectedRows,
                                                    [rowIndex]: !prevSelectedRows[rowIndex],
                                                }));
                                            }}
                                        />
                                    </TableCell>
                                )}
                                {rowIndex !== 0 && (
                                    row.map((column, columnIndex) => {
                                        // Formatar data se a coluna é birthDate
                                        let cellValue = column;
                                        
                                        // Verificar se esta coluna foi mapeada como birthDate
                                        if (columnValue[columnIndex] === 'birthDate' && column) {
                                            try {
                                                // Se for número (Excel serial), converter
                                                if (typeof column === 'number' && column > 1 && column < 100000) {
                                                    const convertedDate = excelSerialToDate(column);
                                                    if (convertedDate && !isNaN(convertedDate.getTime())) {
                                                        // Formatar como DD/MM/YYYY
                                                        const day = String(convertedDate.getDate()).padStart(2, '0');
                                                        const month = String(convertedDate.getMonth() + 1).padStart(2, '0');
                                                        const year = convertedDate.getFullYear();
                                                        cellValue = `${day}/${month}/${year}`;
                                                    }
                                                } 
                                                // Se for string, formatar corretamente
                                                else if (typeof column === 'string' && column.includes('/')) {
                                                    const parts = column.split('/');
                                                    if (parts.length === 3) {
                                                        let first = parseInt(parts[0]);
                                                        let second = parseInt(parts[1]);
                                                        let third = parts[2];
                                                        
                                                        // Se first > 12, definitivamente é DD/MM
                                                        if (first > 12) {
                                                            // Já está DD/MM/YYYY - apenas formatar
                                                            cellValue = `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${third.padStart(4, '0')}`;
                                                        } else {
                                                            // Ambigüidade: pode ser MM/DD ou DD/MM
                                                            // Converter para DD/MM/YYYY assumindo formato americano (MM/DD)
                                                            // e invertendo para brasileiro (DD/MM)
                                                            const year = third.length === 2 ? '19' + third : third.padStart(4, '0');
                                                            // Inverter: MM/DD -> DD/MM
                                                            cellValue = `${String(second).padStart(2, '0')}/${String(first).padStart(2, '0')}/${year}`;
                                                        }
                                                    }
                                                }
                                            } catch (error) {
                                                console.warn('Erro ao formatar data para visualização:', error);
                                            }
                                        }
                                        
                                        return <TableCell key={columnIndex}>{cellValue}</TableCell>;
                                    })
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableCard>
        );
    };


    const handleCloseImport = async () => {
        try {
            history.push("/contacts");
        } catch (err) {
            toastError(err);
        }
    };

    const selectedRowsCount = Object.values(selectedRows).filter(Boolean).length;

    const renderContent = () => {
        return (
            <>
                <div className={clsx(classes.card, classes.options)}>
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Switch checked={validateContact} onChange={(event) => setValidateContact(event.target.checked)} color="primary"/>
                            }
                            label="Validar contatos no WhatsApp"
                        />
                    </FormGroup>
                    <p className={classes.optionsHint}>
                        Em cada coluna, escolha o campo do contato correspondente (Nome e Número são obrigatórios)
                        e marque as linhas que devem ser importadas.
                    </p>
                </div>
                {renderXls()}
                <div className={classes.actions}>
                    <span className={classes.actionsInfo}>
                        {uploading ? (
                            <>
                                <CircularProgress size={16}/>
                                Importando... Aguarde
                            </>
                        ) : (
                            `${selectedRowsCount} de ${Math.max(rows.length - 1, 0)} linha(s) selecionada(s)`
                        )}
                    </span>
                    {error && <div className={classes.error}>{error}</div>}
                    <Button
                        disabled={uploading}
                        onClick={() => {
                            setRows(null);
                            setColumns(null);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={uploading}
                        onClick={() => processImport()}
                    >
                        Importar contatos
                    </Button>
                </div>
            </>
        );
    };

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: onChangeFile,
        maxFiles: 1,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
            'text/plain': ['.txt'],
        }
    });

    const mapping = !imported && !invalidFile && rows && columns;
    const activeStep = imported ? 2 : mapping ? 1 : 0;

    return (
        <div className={classes.root}>
            <ol className={classes.steps} aria-label="Etapas da importação">
                {STEPS.map((label, index) => (
                    <li
                        key={label}
                        className={clsx(
                            classes.step,
                            index === activeStep && classes.stepActive,
                            index < activeStep && classes.stepDone
                        )}
                        aria-current={index === activeStep ? "step" : undefined}
                    >
                        <span className={classes.stepNumber}>{index + 1}</span>
                        {label}
                    </li>
                ))}
            </ol>

            {imported && (
                <div className={clsx(classes.card, classes.centerCard)}>
                    <h3 className={classes.cardTitle}>Resultado da importação</h3>
                    <ul className={classes.resultList}>
                        <li className={clsx(classes.resultItem, countCreated > 0 && classes.resultOk)}>
                            <CheckCircleOutlineIcon/>
                            {countCreated} contatos criados com sucesso
                        </li>
                        <li className={clsx(classes.resultItem, countIgnored > 0 && classes.resultWarn)}>
                            <ReportProblemOutlinedIcon/>
                            {countIgnored} contatos ignorados (número inválido ou não marcados para atualizar)
                        </li>
                    </ul>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setImported(false);
                            setRows(null);
                            setColumns(null);
                            setColumnValue({});
                            setSelectedFields({});
                        }}
                    >
                        Importar mais contatos
                    </Button>
                </div>
            )}
            {openingFile && (
                <div className={clsx(classes.card, classes.centerCard)}>
                    <CircularProgress/>
                    <p className={classes.cardText}>Processando arquivo...</p>
                </div>
            )}
            {invalidFile && (
                <div className={clsx(classes.card, classes.centerCard)} role="alert">
                    <div className={clsx(classes.dropIcon, classes.errorIcon)}>
                        <ErrorOutlineIcon/>
                    </div>
                    <h3 className={classes.cardTitle}>Arquivo inválido!</h3>
                    <p className={classes.cardText}>O arquivo que você tentou importar não é válido. Por favor, tente novamente com um arquivo no formato correto.</p>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setInvalidFile(false);
                            setRows(null);
                            setColumns(null);
                        }}
                    >
                        Tentar novamente
                    </Button>
                </div>
            )}
            {mapping ? renderContent() : (
                !imported && !invalidFile && !openingFile && (
                    <div className={clsx(classes.card, classes.centerCard)}>
                        <div
                            {...getRootProps()}
                            className={clsx("uploaderDrop", classes.dropzone)}
                        >
                            <div className={classes.dropIcon}>
                                <CloudUploadOutlinedIcon/>
                            </div>
                            <h5 className={classes.dropTitle}>Clique ou arraste um arquivo Excel</h5>
                            <p className={classes.dropHint}>
                                Formatos aceitos: xls, xlsx, csv, txt
                            </p>
                            <p className={classes.dropHint}>
                                Para importar contatos, você deve ter pelo menos as colunas de nome e número de telefone.
                            </p>
                        </div>

                        <input {...getInputProps()} />

                        <Button variant="outlined" disabled={uploading} onClick={handleCloseImport}>
                            Voltar
                        </Button>
                    </div>
                )
            )}
        </div>
    );
};

export default ContactImport;
